import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetAllTransactions, useImportTransactions, useSettleTransactions, useGetAllTransactionsExport } from '../hooks/api/transactionsListApi';
import { FormattedDate, FormattedMessage } from 'react-intl';
import Add from '@material-ui/icons/Add';
import DataTableBodyCellBoolean from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellBoolean';
import { transactionsColumns } from '../enums/columns';
import FullList from '../../../components/FullList';
import { useOrganizationEnum } from '../../../hooks/api/organizationEnumApi';
import FileUpload from '../../../components/FileUpload/FileUpload';
import FileExport from '../../../components/FileExport/FileExport';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import { useHistory } from 'react-router';
import useAppContext from '../../../hooks/context/useAppContext';
import userRoles from '../../../constants/userRoles';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import { mapEnumsToFilterList, remapObjectArrayToFilterList } from '../../../utils/objectArray';
import DataTableBodyCellDecimal from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellDecimal';
import { handleUploadResult } from '../../../utils/fileUploadErrorsCheck';

const columns = [
  transactionsColumns.state,
  transactionsColumns.transactionId,
  transactionsColumns.createdAt,
  transactionsColumns.contractId,
  transactionsColumns.organizationName,
  transactionsColumns.commodity,
  transactionsColumns.requestPeriod,
  transactionsColumns.exchangeProductName,
  transactionsColumns.transactionType,
  transactionsColumns.purchaseType,
  transactionsColumns.priceSource,
  transactionsColumns.currency,
  transactionsColumns.quantity,
  transactionsColumns.rate,
  transactionsColumns.price,
];

const transactionNew = 'transaction/new';
const transactionEdit = 'transaction';
const sessionFilterStorageName = 'transactionsFilter';

const TransactionsList = () => {
  const { getAllTransactions, data, loading, error } = useGetAllTransactions();
  const { getAllTransactionsExport, data: exportData, error: exportError, loading: exportLoading } = useGetAllTransactionsExport();
  const { getOrganizationEnum } = useOrganizationEnum();
  const { uploadTransactions, data: importData, loading: importLoading, error: importError } = useImportTransactions();
  const { settleTransactions, data: settleTransactionData } = useSettleTransactions();
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [refresh, setRefresh] = useState(null);
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState({});
  const { user, enums } = useAppContext();

  const storeFilter = JSON.parse(sessionStorage.getItem(sessionFilterStorageName));

  const initialValues = {
    filters: storeFilter?.columnsFilter || {
      organizationId: user?.customerIds,
    },
    page: storeFilter?.page || 0,
    rowsPerPage: storeFilter?.size || user?.initialRowsPerPage,
    order: storeFilter?.order || 'desc',
    orderBy: storeFilter?.orderBy || 'createdAt',
    fulltext: storeFilter?.fulltext || '',
    filterOptions: storeFilter?.filterOptions || {},
  };

  const checkboxTools = [
    {
      onClick: (transactions) => {
        settleTransactions(transactions);
      },
      icon: <DoneAllIcon />,
      label: <FormattedMessage id={'app.dataTable.toolbarIconTooltip.dealWith'} />,
      tooltip: <FormattedMessage id={'app.dataTable.toolbarIconTooltip.done'} />,
    },
  ];

  const toolbarComponents = [
    <FileUpload upload={uploadTransactions} tooltip={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.import'} />} key={0} />,
    <FileExport
      getAllEntityExport={getAllTransactionsExport}
      tooltip={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.export'} />}
      fileName={'EEM_transakce_export.xlsx'}
      data={exportData}
      searchQuery={searchQuery}
      exportError={exportError}
      setErrorMessage={setErrorMessage}
      key={'toolbar-file-export'}
    />,
    <Tooltip title={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.add'} />} arrow key={1}>
      <IconButton size={'small'} onClick={() => history.push(`/${transactionNew}`)}>
        <Add fontSize={'large'} />
      </IconButton>
    </Tooltip>,
  ];

  useEffect(() => {
    handleUploadResult(importData, importError, setSuccessMessage, setErrorMessage, setRefresh);
  }, [importData, importError]);

  useEffect(() => {
    if (settleTransactionData) {
      setRefresh(true);
    }
  }, [settleTransactionData]);

  const processData = (data) => {
    return {
      data: data.data.map((row) => {
        return {
          id: row.id,
          state: <DataTableBodyCellBoolean value={row.state} />,
          transactionId: row.id,
          createdAt: <FormattedDate value={row.createdAt} />,
          idExternal: row.idExternal,
          organizationName: row.contract?.customer?.name,
          period: row.contract?.contractPeriod,
          commodity: row.contract?.commodity?.name,
          exchangeProductName: row.exchangeProduct?.name,
          transactionType: row.transactionType?.name,
          purchaseType: row.purchaseType?.name,
          priceSource: row.priceSource?.name,
          currency: row.currency?.id,
          quantity: <DataTableBodyCellDecimal value={row.quantity} />,
          rate: <DataTableBodyCellDecimal value={row.rate} />,
          price: <DataTableBodyCellDecimal value={row.price} />,
        };
      }),
      totalCount: data.totalCount,
    };
  };

  const columnFilters = useMemo(
    () => ({
      [transactionsColumns.organizationName.id]: async () => getOrganizationEnum(),
      [transactionsColumns.organizationName.createdAt]: () => {},
      [transactionsColumns.state.id]: () => [
        {
          id: '0',
          label: 'Ne',
        },
        {
          id: '1',
          label: 'Ano',
        },
      ],
      [transactionsColumns.commodity.id]: () => mapEnumsToFilterList(enums?.['commodities']),
      [transactionsColumns.purchaseType.id]: () => mapEnumsToFilterList(enums?.['purchaseTypes']),
      [transactionsColumns.priceSource.id]: () => mapEnumsToFilterList(enums?.['priceSources']),
      [transactionsColumns.currency.id]: () => mapEnumsToFilterList(enums?.['currencies']),
      [transactionsColumns.requestPeriod.id]: () => remapObjectArrayToFilterList(enums?.['years'], 'year', 'year'),
      [transactionsColumns.transactionType.id]: () => remapObjectArrayToFilterList(enums?.['transactionTypes'], 'id', 'name'),
      [transactionsColumns.exchangeProductName.id]: () => remapObjectArrayToFilterList(enums?.['exchangeProduct'], 'name', 'name'),
    }),
    [getOrganizationEnum, enums],
  );

  const onFilterOpen = useCallback(
    async (column) => {
      return columnFilters?.[column] && columnFilters[column]();
    },
    [columnFilters],
  );

  return (
    <FullList
      getData={getAllTransactions}
      checkboxColumn
      loading={loading || importLoading || exportLoading}
      error={error}
      data={data}
      processData={processData}
      columns={columns}
      initialValues={initialValues}
      showToolbarIcons={user.role === userRoles.ADMIN}
      onFilterOpen={onFilterOpen}
      toolbarComponents={toolbarComponents}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      refresh={refresh}
      setRefresh={setRefresh}
      onLineClick={(line) => {
        history.push(`/${transactionEdit}/${line.id}`);
      }}
      checkboxTools={checkboxTools}
      setSearchQuery={setSearchQuery}
      sessionFilterStorageName={sessionFilterStorageName}
    />
  );
};

TransactionsList.propTypes = {};

export default TransactionsList;
